'use client';

import { AppIcon } from '@impacta/shared';
import { TIERS, type TierKey } from '@impacta/shared';

export interface TierInfo {
  id: string;
  name: string;
  price: number;
  description: string;
  tierKey: TierKey;
}

interface GiftCardTierSelectorProps {
  tiers: TierInfo[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export default function GiftCardTierSelector({
  tiers,
  selectedId,
  onSelect,
}: GiftCardTierSelectorProps) {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {tiers.map((tier) => {
          const isSelected = selectedId === tier.id;
          const config = TIERS[tier.tierKey];

          return (
            <button
              key={tier.id}
              type="button"
              onClick={() => onSelect(tier.id)}
              className={`relative rounded-3xl text-left transition-all duration-300 group ${
                isSelected
                  ? 'p-[2px] shadow-xl tier-border-animate'
                  : 'p-0 shadow-md hover:shadow-xl hover:scale-[1.02] border border-[#E8E8E4]'
              }`}
              style={
                isSelected
                  ? {
                      background: `linear-gradient(135deg, ${config.colors.from}, ${config.colors.to}, ${config.colors.from})`,
                      backgroundSize: '200% 200%',
                    }
                  : undefined
              }
            >
              <div
                className={`bg-white p-6 h-full ${isSelected ? 'rounded-[22px]' : 'rounded-[23px]'}`}
              >
                {/* Icon */}
                <div
                  className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 transition-colors ${
                    isSelected
                      ? 'text-white'
                      : 'bg-[#FAFAF8] text-[#00A19B] group-hover:bg-[#00A19B]/10'
                  }`}
                  style={
                    isSelected
                      ? {
                          background: `linear-gradient(135deg, ${config.colors.from}, ${config.colors.to})`,
                        }
                      : undefined
                  }
                >
                  <AppIcon name={config.icon as any} size={32} />
                </div>

                {/* Name */}
                <h3 className="text-xl font-bold text-[#373A36] mb-2">Giftcard {tier.name}</h3>

                {/* Price */}
                <p className="text-2xl font-extrabold mb-3" style={{ color: config.colors.from }}>
                  {config.displayPrice}
                </p>

                {/* Description */}
                <p className="text-sm text-[#6b6e68] leading-relaxed">{tier.description}</p>

                {/* Selected indicator */}
                {isSelected && (
                  <div
                    className="mt-4 flex items-center gap-2 text-sm font-semibold"
                    style={{ color: '#373A36' }}
                  >
                    <div
                      className="w-2 h-2 rounded-full animate-pulse"
                      style={{ background: config.colors.to }}
                    />
                    Seleccionada
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <style>{`
        @keyframes tier-gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .tier-border-animate {
          animation: tier-gradient-shift 3s ease-in-out infinite;
        }
      `}</style>
    </>
  );
}
